import { useState, useEffect, useMemo } from "react";
import { Grid3x3, Save, Search } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, SectionCard } from "@/components/ui-helpers";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const BUCKETS = [
  ["required_attributes", "Required"],
  ["optional_attributes", "Optional"],
  ["variant_attributes", "Variant"],
  ["filters", "Filter"],
];
const CUSTOM_PRESET = ["design-upload", "custom-text", "logo-upload", "photo-upload", "name", "message", "dimensions", "color-choice", "reference-image", "delivery-date"];

export function AdminCatalog() {
  const [attrs, setAttrs] = useState([]);
  const [meta, setMeta] = useState({ product_types: [], purchase_models: [], fulfilment_types: [] });
  const [cats, setCats] = useState([]);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [path, setPath] = useState([]);
  const [saving, setSaving] = useState(false);
  const [versions, setVersions] = useState([]);
  const [complianceInput, setComplianceInput] = useState("");
  const [diffA, setDiffA] = useState("");
  const [diffB, setDiffB] = useState("");

  useEffect(() => {
    api.get("/catalog/attributes").then((r) => { setAttrs(r.data.attributes); setMeta({ product_types: r.data.product_types, purchase_models: r.data.purchase_models, fulfilment_types: r.data.fulfilment_types }); }).catch(() => {});
    api.get("/marketplace/categories").then((r) => setCats(r.data.categories)).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return s ? cats.filter((c) => (c.name || "").toLowerCase().includes(s)).slice(0, 30) : cats.slice(0, 30);
  }, [q, cats]);

  const loadConfig = async (c) => {
    setSel(c);
    try {
      const r = await api.get(`/catalog/categories/${c.category_id}/config`);
      const cc = r.data.config;
      setPath(r.data.category.path || []);
      setCfg({
        required_attributes: cc.required_attributes.map((a) => a.key),
        optional_attributes: cc.optional_attributes.map((a) => a.key),
        variant_attributes: cc.variant_attributes.map((a) => a.key),
        filters: cc.filters.map((a) => a.key),
        product_types: cc.product_types, purchase_models: cc.purchase_models, fulfilment_types: cc.fulfilment_types,
        customization_fields: cc.customization_fields || [], compliance_docs: cc.compliance_docs || [],
        ai_assist_enabled: cc.ai_assist_enabled, web_research_enabled: cc.web_research_enabled,
      });
      api.get(`/admin/catalog/categories/${c.category_id}/config/versions`).then((v) => { const vs = v.data.versions; setVersions(vs); if (vs.length >= 2) { setDiffB(String(vs[0].version)); setDiffA(String(vs[1].version)); } else { setDiffA(""); setDiffB(""); } }).catch(() => setVersions([]));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggle = (bucket, key) => {
    setCfg((c) => {
      const has = c[bucket].includes(key);
      return { ...c, [bucket]: has ? c[bucket].filter((k) => k !== key) : [...c[bucket], key] };
    });
  };

  const save = async () => {
    if (!sel) return;
    setSaving(true);
    try {
      const r = await api.put(`/admin/catalog/categories/${sel.category_id}/config`, cfg);
      toast.success(`Saved — config v${r.data.config_version}`);
      api.get(`/admin/catalog/categories/${sel.category_id}/config/versions`).then((v) => setVersions(v.data.versions)).catch(() => {});
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setSaving(false);
  };

  const applyToSiblings = async () => {
    if (!sel || !window.confirm("Copy this category's configuration to all sibling categories (same parent)? Each gets a new version.")) return;
    try { const r = await api.post(`/admin/catalog/categories/${sel.category_id}/apply-to-siblings`); toast.success(`Applied to ${r.data.applied_to} sibling categor${r.data.applied_to === 1 ? "y" : "ies"}`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const chipRow = (label, items, bucket) => (    <div><p className="text-xs font-semibold text-slate-500">{label}</p><div className="mt-1 flex flex-wrap gap-1.5">{items.map((t) => <button key={t} onClick={() => toggle(bucket, t)} data-testid={`catalog-${bucket}-${t}`} className={`rounded-full px-2.5 py-1 text-xs capitalize ${cfg[bucket].includes(t) ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-500"}`}>{t.replace(/-/g, " ")}</button>)}</div></div>
  );

  const DIFF_ARR = [["required_attributes", "Required attrs"], ["optional_attributes", "Optional attrs"], ["variant_attributes", "Variant attrs"], ["filters", "Filters"], ["product_types", "Product types"], ["purchase_models", "Purchase models"], ["fulfilment_types", "Fulfilment"], ["customization_fields", "Customization"], ["compliance_docs", "Compliance docs"]];
  const DIFF_BOOL = [["ai_assist_enabled", "AI Assist"], ["web_research_enabled", "Web Research"]];
  const snapOf = (ver) => versions.find((v) => String(v.version) === String(ver))?.snapshot || {};
  const configDiff = useMemo(() => {
    if (!diffA || !diffB || diffA === diffB) return null;
    const va = snapOf(diffA), vb = snapOf(diffB);
    const changes = [];
    DIFF_ARR.forEach(([k, label]) => {
      const sa = new Set(va[k] || []), sb = new Set(vb[k] || []);
      const added = [...sb].filter((x) => !sa.has(x)), removed = [...sa].filter((x) => !sb.has(x));
      if (added.length || removed.length) changes.push({ key: k, label, added, removed });
    });
    DIFF_BOOL.forEach(([k, label]) => { const a = va[k] ?? true, b = vb[k] ?? true; if (a !== b) changes.push({ key: k, label, changed: `${a ? "On" : "Off"} → ${b ? "On" : "Off"}` }); });
    return changes;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [diffA, diffB, versions]);

  return (
    <div className="space-y-6" data-testid="admin-catalog-page">
      <PageHeader title="Product Configuration" icon={Grid3x3} subtitle="Central category, attribute and variant configuration. These rules drive the seller Add-Product wizard, AI Assist and customer filters. Inheritance flows parent → child." />

      <div className="grid gap-6 lg:grid-cols-[320px_1fr]">
        <SectionCard title={`Categories (${cats.length})`} icon={Search} testid="catalog-categories">
          <div className="relative mb-2"><Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" /><Input value={q} onChange={(e) => setQ(e.target.value)} data-testid="catalog-search" placeholder="Search category…" className="pl-8" /></div>
          <div className="max-h-[520px] space-y-1 overflow-y-auto">
            {filtered.map((c) => (
              <button key={c.category_id} onClick={() => loadConfig(c)} data-testid={`catalog-cat-${c.slug}`} className={`block w-full rounded-lg px-3 py-1.5 text-left text-sm ${sel?.category_id === c.category_id ? "bg-slate-900 text-white" : "hover:bg-slate-50 text-slate-700"}`}>
                {c.name} {c.config_version ? <span className="ml-1 text-[10px] opacity-70">v{c.config_version}</span> : null}
              </button>
            ))}
          </div>
        </SectionCard>

        <SectionCard title={sel ? `Configuration — ${sel.name}` : "Select a category"} icon={Grid3x3} testid="catalog-config">
          {!cfg && <p className="py-6 text-sm text-slate-400">Pick a category on the left to view and edit its attribute, variant and filter configuration.</p>}
          {cfg && (
            <div className="space-y-4">
              <p className="text-xs text-slate-400" data-testid="catalog-path">{path.map((p) => p.name).join(" › ")}</p>
              <div className="flex flex-wrap gap-2 text-xs">
                {meta.product_types.map((t) => (
                  <button key={t} onClick={() => toggle("product_types", t)} className={`rounded-full px-3 py-1 font-semibold capitalize ${cfg.product_types.includes(t) ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-500"}`}>{t.replace(/-/g, " ")}</button>
                ))}
              </div>
              <div className="space-y-3" data-testid="catalog-editors">
                {chipRow("Purchase models", meta.purchase_models, "purchase_models")}
                {chipRow("Fulfilment", meta.fulfilment_types, "fulfilment_types")}
                {chipRow("Customization fields", CUSTOM_PRESET, "customization_fields")}
                <div>
                  <p className="text-xs font-semibold text-slate-500">Compliance documents</p>
                  <div className="mt-1 flex flex-wrap items-center gap-1.5">
                    {cfg.compliance_docs.map((d) => <span key={d} className="rounded-full bg-amber-100 px-2.5 py-1 text-xs text-amber-700" data-testid={`catalog-compliance-${d}`}>{d} <button onClick={() => setCfg((c) => ({ ...c, compliance_docs: c.compliance_docs.filter((x) => x !== d) }))} className="ml-0.5">×</button></span>)}
                    <Input value={complianceInput} onChange={(e) => setComplianceInput(e.target.value)} data-testid="catalog-compliance-input" placeholder="e.g. fssai" className="h-8 w-40" />
                    <button onClick={() => { const v = complianceInput.trim().toLowerCase().replace(/\s+/g, "-"); if (v) { setCfg((c) => ({ ...c, compliance_docs: [...new Set([...c.compliance_docs, v])] })); setComplianceInput(""); } }} data-testid="catalog-compliance-add" className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-600">Add</button>
                  </div>
                </div>
                {versions.length > 0 && (
                  <details data-testid="catalog-versions"><summary className="cursor-pointer text-xs font-semibold text-slate-500">Version history & diff ({versions.length})</summary>
                    <div className="mt-1 space-y-0.5">{versions.map((v) => <p key={v.version} className="text-[11px] text-slate-400">v{v.version} · {new Date(v.effective_date).toLocaleString()} · {v.created_by}</p>)}</div>
                    {versions.length >= 2 && (
                      <div className="mt-2 rounded-lg border border-slate-100 p-2" data-testid="catalog-diff">
                        <div className="flex flex-wrap items-center gap-2 text-xs">
                          <span className="font-semibold text-slate-500">Compare</span>
                          <select value={diffA} onChange={(e) => setDiffA(e.target.value)} data-testid="catalog-diff-a" className="rounded border border-slate-200 px-2 py-1">{versions.map((v) => <option key={v.version} value={v.version}>v{v.version}</option>)}</select>
                          <span className="text-slate-400">→</span>
                          <select value={diffB} onChange={(e) => setDiffB(e.target.value)} data-testid="catalog-diff-b" className="rounded border border-slate-200 px-2 py-1">{versions.map((v) => <option key={v.version} value={v.version}>v{v.version}</option>)}</select>
                        </div>
                        {diffA === diffB ? <p className="mt-2 text-[11px] text-slate-400">Pick two different versions.</p> : configDiff && configDiff.length === 0 ? <p className="mt-2 text-[11px] text-slate-400" data-testid="catalog-diff-empty">No differences between v{diffA} and v{diffB}.</p> : (
                          <div className="mt-2 space-y-1" data-testid="catalog-diff-result">
                            {(configDiff || []).map((c, i) => (
                              <div key={i} className="text-[11px]" data-testid={`catalog-diff-${c.key}`}>
                                <span className="font-semibold text-slate-600">{c.label}: </span>
                                {c.changed ? <span className="text-slate-500">{c.changed}</span> : <>
                                  {c.added?.map((x) => <span key={`a${x}`} className="mr-1 rounded bg-emerald-50 px-1 text-emerald-700">+ {x}</span>)}
                                  {c.removed?.map((x) => <span key={`r${x}`} className="mr-1 rounded bg-rose-50 px-1 text-rose-700 line-through">− {x}</span>)}
                                </>}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </details>
                )}
              </div>
              <div className="overflow-x-auto rounded-xl border border-slate-100">
                <table className="w-full text-left text-xs" data-testid="catalog-attr-table">
                  <thead><tr className="bg-slate-50 text-slate-500"><th className="px-3 py-2">Attribute</th>{BUCKETS.map(([, l]) => <th key={l} className="px-2 py-2 text-center">{l}</th>)}</tr></thead>
                  <tbody>
                    {attrs.map((a) => (
                      <tr key={a.key} className="border-t border-slate-50" data-testid={`catalog-attr-${a.key}`}>
                        <td className="px-3 py-1.5">{a.name} <span className="text-[10px] text-slate-400">{a.data_type}{a.variant_eligible ? " · variant-ok" : ""}</span></td>
                        {BUCKETS.map(([bk]) => (
                          <td key={bk} className="px-2 py-1.5 text-center">
                            <input type="checkbox" checked={cfg[bk].includes(a.key)} onChange={() => toggle(bk, a.key)} data-testid={`catalog-${bk}-${a.key}`}
                              disabled={bk === "variant_attributes" && !a.variant_eligible} />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <details className="rounded-xl border border-slate-100 p-2" data-testid="catalog-preview"><summary className="cursor-pointer text-xs font-semibold text-slate-500">Preview seller form ({cfg.required_attributes.length + cfg.optional_attributes.length} fields)</summary><div className="mt-2 grid gap-1 sm:grid-cols-2">{[...cfg.required_attributes.map((k) => [k, true]), ...cfg.optional_attributes.map((k) => [k, false])].map(([k, req]) => { const a = attrs.find((x) => x.key === k); return <div key={k} className="rounded border border-slate-100 px-2 py-1 text-xs text-slate-600" data-testid={`catalog-preview-${k}`}>{a ? a.name : k}{req ? " *" : ""} <span className="text-[10px] text-slate-400">{a ? a.data_type : ""}</span></div>; })}</div></details>
              <div className="flex flex-wrap gap-2">
                <button onClick={save} disabled={saving} data-testid="catalog-save" className="inline-flex items-center gap-1.5 rounded-full brand-gradient-bg px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-50"><Save className="h-4 w-4" /> {saving ? "Saving…" : "Save configuration"}</button>
                <button onClick={applyToSiblings} data-testid="catalog-apply-siblings" className="rounded-full border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50">Copy to sibling categories</button>
              </div>
            </div>
          )}
        </SectionCard>
      </div>
    </div>
  );
}
